<template>
	<v-layout fill-height wrap>
		<v-flex md12>
			<employee-assigned-forms-list
				v-if="loggedInEmployee && loggedInTenant"
				ref="employeeAssignedFormsList"
				:forms="assignedForms"
				:available-forms="availableForms"
				:employees="[]"
				:employee-id="loggedInEmployee.id"
				:is-loading="isListingAssignedForms || isListingSelfAssignableForms"
				:is-starting-self-assignment="isStartingSelfAssignment"
				:can-assign="false"
				:self-assignable-forms="selfAssignableForms"
				:employee-name="loggedInEmployee.fullName"
				:employee-first-name="loggedInEmployee.firstName"
				:tenant-id="loggedInTenant.id"
				:is-employee-respondent="true"
				@openSelfAssignableForms="ensureSelfAssignableFormsLoaded"
				@startSelfAssignment="startSelfAssignment"
				@openSubmission="openEmbeddedSubmission"
			/>
		</v-flex>

		<embedded-forms-app-dialog
			v-if="embeddedForms.dialog"
			:dialog="embeddedForms.dialog"
			:title="embeddedForms.title"
			:path="embeddedForms.path"
			@close="closeEmbeddedForms"
			@authLoginStarted="onEmbeddedFormsAuthStarted"
			@authLoginFailed="onEmbeddedFormsAuthFailed"
			@submissionCompleted="onEmbeddedSubmissionCompleted"
		/>
	</v-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import EmployeeAssignedFormsList from '@/components/forms/EmployeeAssignedFormsList.vue';
import EmbeddedFormsAppDialog from '@/components/forms/EmbeddedFormsAppDialog.vue';
import toast from '@/services/toasts/index.js';

export default {
	name: 'PageMyForms',
	components: {
		EmployeeAssignedFormsList,
		EmbeddedFormsAppDialog
	},
	data() {
		return {
			embeddedForms: {
				dialog: false,
				title: 'Complete Form',
				path: ''
			}
		};
	},
	computed: {
		...mapGetters({
			loggedInEmployee: 'loggedInEmployee',
			loggedInTenant: 'loggedInTenant',
			selfAssignableForms: 'forms/selfAssignableForms',
			isListingSelfAssignableForms: 'forms/isListingSelfAssignable',
			isListingAssignedForms: 'forms/isListingAssignedToEmployee',
			isStartingSelfAssignment: 'forms/isStartingSelfAssignment'
		}),
		assignedForms() {
			return this.loggedInEmployee
				? this.$store.getters['forms/assignedFormsForEmployee'](this.loggedInEmployee.id)
				: [];
		},
		availableForms() {
			return this.selfAssignableForms
				.filter(form => !!form && !!form.id && form.status === 'Active')
				.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
		}
	},
	watch: {
		loggedInEmployee(value, previousValue) {
			if (value && (!previousValue || value.id !== previousValue.id)) {
				this.loadForms();
			}
		}
	},
	methods: {
		async loadForms() {
			if (!this.loggedInEmployee) return;

			await Promise.all([
				this.$store.dispatch('forms/listAssignedToEmployee', this.loggedInEmployee.id),
				this.$store.dispatch('forms/listSelfAssignable')
			]);
		},
		async ensureSelfAssignableFormsLoaded() {
			if (this.selfAssignableForms.length === 0) {
				await this.$store.dispatch('forms/listSelfAssignable');
			}
		},
		getSubmissionId(result) {
			const data = result && result.data ? result.data : null;
			if (!data) return null;
			return data.id || data.submissionId || data.Id || data.SubmissionId;
		},
		async startSelfAssignment(form) {
			if (!form || !form.id || !this.loggedInEmployee) return;

			const result = await this.$store.dispatch('forms/startSelfAssignment', form.id);
			if (!result.isSuccess) return;

			await this.$store.dispatch('forms/listAssignedToEmployee', this.loggedInEmployee.id);

			if (this.$refs.employeeAssignedFormsList) {
				this.$refs.employeeAssignedFormsList.closeSelfAssignableDialog();
			}

			this.openEmbeddedSubmission({
				submissionId: this.getSubmissionId(result),
				isReadonly: false
			});
		},
		openEmbeddedSubmission({ submissionId, isReadonly }) {
			if (!this.loggedInTenant || !submissionId) return;

			const basePath = `/internal/forms/${this.loggedInTenant.id}/${submissionId}`;
			this.embeddedForms = {
				dialog: true,
				title: isReadonly ? 'View Submission' : 'Complete Form',
				path: isReadonly ? `${basePath}/view` : basePath
			};
		},
		closeEmbeddedForms() {
			this.embeddedForms.dialog = false;
		},
		onEmbeddedFormsAuthStarted() {
			toast.info('Signing you in to forms...');
		},
		onEmbeddedFormsAuthFailed(payload) {
			toast.error(payload && payload.message ? payload.message : 'Unable to sign you in to forms.');
		},
		async onEmbeddedSubmissionCompleted() {
			if (this.loggedInEmployee) {
				await this.$store.dispatch('forms/listAssignedToEmployee', this.loggedInEmployee.id);
			}
			toast.success('Form submission completed');
			this.closeEmbeddedForms();
		}
	},
	async created() {
		await this.$store.dispatch('setPageTitle', 'My Forms');
		this.loadForms();
	}
};
</script>
